/**
 * Landing Page Component
 * Based on Artifact #5: Offer Landing Page
 * Brand: #6B5E2B Gold / #2C3E50 Charcoal / Raleway + Fairplay Display
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleReferralSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/referral-submit');
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Raleway', 'Fairplay Display', sans-serif" }}>
      {/* Hero Section */}
      <section className="hero" style={{
        background: 'linear-gradient(135deg, #4a6580 0%, #3a5468 100%)',
        color: 'white',
        padding: '80px 20px',
        textAlign: 'center',
      }}>
        <h1 style={{
          fontSize: '3rem',
          marginBottom: '20px',
          fontFamily: "'Fairplay Display', serif",
        }}>
          Get a FREE 1-Pager Website
        </h1>
        <p style={{
          fontSize: '1.3rem',
          marginBottom: '30px',
          opacity: '0.95',
        }}>
          Professional website worth R3,997 — FREE for business owners who refer 5 friends
        </p>
        <button
          onClick={handleReferralSubmit}
          className="cta-button"
          style={{
            display: 'inline-block',
            padding: '18px 40px',
            backgroundColor: '#6e5c25',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
        >
          Refer 5 Friends & Get Your FREE Website
        </button>
      </section>

      {/* What You Get Section */}
      <section className="value-prop" style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 20px',
      }}>
        <h2 style={{
          textAlign: 'center',
          color: '#4a6580',
          fontSize: '2rem',
          marginBottom: '40px',
        }}>
          What You Get
        </h2>
        <div className="benefits-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '30px',
          marginBottom: '40px',
        }}>
          <div className="benefit-card" style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '30px',
            textAlign: 'center',
            border: '1px solid #ddd',
          }}>
            <div className="benefit-icon" style={{ fontSize: '2.5rem', marginBottom: '15px' }}>
              🎨
            </div>
            <h3 style={{ color: '#4a6580', marginBottom: '10px' }}>
              Professional Design
            </h3>
            <p>
              Custom 1-pager designed for YOUR business with your brand colors and style
            </p>
          </div>

          <div className="benefit-card" style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '30px',
            textAlign: 'center',
            border: '1px solid #ddd',
          }}>
            <div className="benefit-icon" style={{ fontSize: '2.5rem', marginBottom: '15px' }}>
              📱
            </div>
            <h3 style={{ color: '#4a6580', marginBottom: '10px' }}>
              Mobile Responsive
            </h3>
            <p>
              Looks great on phones, tablets, and desktops — all devices covered
            </p>
          </div>

          <div className="benefit-card" style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '30px',
            textAlign: 'center',
            border: '1px solid #ddd',
          }}>
            <div className="benefit-icon" style={{ fontSize: '2.5rem', marginBottom: '15px' }}>
              🔍
            </div>
            <h3 style={{ color: '#4a6580', marginBottom: '10px' }}>
              SEO Optimized
            </h3>
            <p>
              Optimized for Google so customers can find you when they search
            </p>
          </div>

          <div className="benefit-card" style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            padding: '30px',
            textAlign: 'center',
            border: '1px solid #ddd',
          }}>
            <div className="benefit-icon" style={{ fontSize: '2.5rem', marginBottom: '15px' }}>
              ☁️
            </div>
            <h3 style={{ color: '#4a6580', marginBottom: '10px' }}>
              Lifetime Hosting
            </h3>
            <p>
              Free hosting forever — no monthly fees, no hidden costs
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '60px 20px',
      }}>
        <h2 style={{
          textAlign: 'center',
          color: '#4a6580',
          fontSize: '2rem',
          marginBottom: '40px',
        }}>
          How It Works
        </h2>

        {/* Step 1 */}
        <div className="step" style={{
          display: 'flex',
          marginBottom: '30px',
          alignItems: 'flex-start',
        }}>
          <div className="step-number" style={{
            background: '#4a6580',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            marginRight: '20px',
            flexShrink: '0',
          }}>
            1
          </div>
          <div className="step-content">
            <h3 style={{ color: '#4a6580', marginBottom: '5px' }}>
              Share Your Referral Link
            </h3>
            <p>
              Send your personalized link to 5 business owner friends
            </p>
          </div>
        </div>

        {/* Step 2 */}
        <div className="step" style={{
          display: 'flex',
          marginBottom: '30px',
          alignItems: 'flex-start',
        }}>
          <div className="step-number" style={{
            background: '#4a6580',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            marginRight: '20px',
            flexShrink: '0',
          }}>
            2
          </div>
          <div className="step-content">
            <h3 style={{ color: '#4a6580', marginBottom: '5px' }}>
              Friends Accept the Offer
            </h3>
            <p>
              Each friend accepts the FREE website offer and creates an account
            </p>
          </div>
        </div>

        {/* Step 3 */}
        <div className="step" style={{
          display: 'flex',
          marginBottom: '30px',
          alignItems: 'flex-start',
        }}>
          <div className="step-number" style={{
            background: '#4a6580',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            marginRight: '20px',
            flexShrink: '0',
          }}>
            3
          </div>
          <div className="step-content">
            <h3 style={{ color: '#4a6580', marginBottom: '5px' }}>
              Complete Your Questionnaire
            </h3>
            <p>
              Answer 5 quick questions about your business (5 minutes)
            </p>
          </div>
        </div>

        {/* Step 4 */}
        <div className="step" style={{
          display: 'flex',
          marginBottom: '30px',
          alignItems: 'flex-start',
        }}>
          <div className="step-number" style={{
            background: '#4a6580',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            marginRight: '20px',
            flexShrink: '0',
          }}>
            4
          </div>
          <div className="step-content">
            <h3 style={{ color: '#4a6580', marginBottom: '5px' }}>
              AI Generates Your Website
            </h3>
            <p>
              Our AI creates your custom 1-pager in 1-2 minutes
            </p>
          </div>
        </div>

        {/* Step 5 */}
        <div className="step" style={{
          display: 'flex',
          marginBottom: '30px',
          alignItems: 'flex-start',
        }}>
          <div className="step-number" style={{
            background: '#4a6580',
            color: 'white',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            marginRight: '20px',
            flexShrink: '0',
          }}>
            5
          </div>
          <div className="step-content">
            <h3 style={{ color: '#4a6580', marginBottom: '5px' }}>
              Expert Reviews & Deploys
            </h3>
            <p>
              Human expert reviews your site and deploys it within 24-48 hours
            </p>
          </div>
        </div>
      </section>

      {/* Questions Answered Section */}
      <section className="questions-answered" style={{
        maxWidth: '700px',
        margin: '0 auto',
        padding: '40px 20px',
        background: '#e3f2fd',
        borderRadius: '8px',
        marginBottom: '40px',
      }}>
        <h3 style={{ color: '#4a6580', marginBottom: '15px' }}>
          Questions Answered
        </h3>
        <ul style={{ listStyle: 'none', padding: '0' }}>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #bbdefb' }}>
            <strong>What do I get?</strong> — A FREE professional 1-pager website with lifetime hosting
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #bbdefb' }}>
            <strong>What do I need to do?</strong> — Refer 5 friends who also accept the free offer
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #bbdefb' }}>
            <strong>How long will it take?</strong> — 24-48 hours from questionnaire to live website
          </li>
          <li style={{ padding: '8px 0', borderBottom: '1px solid #bbdefb' }}>
            <strong>Is there a catch?</strong> — No hidden fees, no long-term commitments
          </li>
          <li style={{ padding: '8px 0', borderBottom: 'none' }}>
            <strong>Who can I contact?</strong> — support@chrysalinks.co.za or WhatsApp +27 81 714 2228
          </li>
        </ul>
      </section>

      {/* CTA Section */}
      <section className="cta-section" id="cta" style={{
        padding: '60px 20px',
        textAlign: 'center',
      }}>
        <h2 style={{ color: '#4a6580', fontSize: '1.8rem', marginBottom: '20px' }}>
          Ready to Get Your FREE Website?
        </h2>
        <p style={{ marginBottom: '30px', color: '#666' }}>
          Refer 5 friends and unlock your professional 1-pager website — completely FREE
        </p>
        <button
          onClick={handleReferralSubmit}
          className="cta-button"
          style={{
            display: 'inline-block',
            padding: '18px 40px',
            backgroundColor: '#6e5c25',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '1.2rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
        >
          Start Now — Refer 5 Friends
        </button>
      </section>

      {/* Support Section */}
      <section className="support" style={{
        textAlign: 'center',
        padding: '40px 20px',
        color: '#666',
      }}>
        <p>
          Questions? Contact us:{' '}
          <a href="mailto:support@chrysalinks.co.za" style={{ color: '#4a6580', textDecoration: 'none' }}>
            support@chrysalinks.co.za
          </a>{' '}
          |{' '}
          <a href="https://wa.me/27817142228" style={{ color: '#4a6580', textDecoration: 'none' }}>
            WhatsApp +27 81 714 2228
          </a>
        </p>
      </section>
    </div>
  );
};

export default LandingPage;